<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { countFillableFields, pageOrientation } from '@record-fill';
  import {
    FormBuilderViewerStage,
    FormBuilderFooter,
    useFormBuilderStore,
    normalizeAnchorFieldIds,
    type FormStructure,
  } from '@/features/FormBuilder';

  const props = defineProps<{
    structure: FormStructure;
    rulesStructure?: { anchorFieldIds?: unknown } | null;
  }>();

  const formBuilderStore = useFormBuilderStore();

  const zoom = ref(1);
  const pageCount = ref(1);
  const currentPage = ref(1);
  const viewScrollTop = ref(0);
  // Lives here, and not in the rules store, so entering the screen never touches the rules draft
  // of whoever is building forms. It starts off on every entry.
  const showAnchorHighlights = ref(false);

  const scrollRef = ref<HTMLElement | null>(null);
  const canvasWrapperRef = ref<HTMLElement | null>(null);
  const canvasOffsetTop = ref<number | null>(null);

  const totalFields = computed(() => countFillableFields(formBuilderStore.elements));
  const orientation = computed(() => pageOrientation(props.structure));

  // A record with no rules, or with a rules structure the strict checker would reject, answers an
  // empty list: no marking at all, and no broken screen.
  const anchorFieldIds = computed(() =>
    normalizeAnchorFieldIds(props.rulesStructure?.anchorFieldIds)
  );

  function handleScroll(): void {
    const scrollEl = scrollRef.value;
    const canvasEl = canvasWrapperRef.value;
    if (!scrollEl || !canvasEl) return;
    if (canvasOffsetTop.value === null) {
      canvasOffsetTop.value =
        canvasEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
    }
    viewScrollTop.value = Math.max(0, scrollEl.scrollTop - canvasOffsetTop.value);
  }
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div
      ref="scrollRef"
      class="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] px-8 py-6"
      data-testid="record-form-viewer-scroll"
      @scroll="handleScroll"
    >
      <div ref="canvasWrapperRef">
        <FormBuilderViewerStage
          :structure="props.structure"
          :zoom="zoom"
          :view-scroll-top="viewScrollTop"
          force-readonly
          restore-draft-on-unmount
          :anchor-field-ids="anchorFieldIds"
          :show-anchor-highlights="showAnchorHighlights"
          @update:zoom="zoom = $event"
          @update:page-count="pageCount = $event"
          @update:current-page="currentPage = $event"
        />
      </div>
    </div>

    <FormBuilderFooter
      class="shrink-0"
      mode="view"
      :orientation="orientation"
      :zoom="zoom"
      :page-count="pageCount"
      :current-page="currentPage"
      :total-fields="totalFields"
      enable-anchor-tool
      :show-anchor-highlights="showAnchorHighlights"
      @update:zoom="zoom = $event"
      @toggle-anchor-visibility="showAnchorHighlights = !showAnchorHighlights"
    />
  </div>
</template>
